import { Card, CardBody, Col, Container, Input, Label, Row } from 'reactstrap'
import DataTable, { TableColumn } from "react-data-table-component";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
} from "reactstrap";
import { useEffect, useMemo, useState } from 'react';
import { SearchTableButton } from '../../../../utils/Constant';
import api from '../../../../utils/api';
import Swal from 'sweetalert2';
import { CouponListTableDataColumnType } from '../../../../Types/Application/Ecommerce/Ecommerce';
import { SVG } from '../../../../AbstractElements';
import CouponListFilterHeader from './CouponListFilterHeader';
import { CouponListTableOfferComponent } from '../../../../Data/Application/Ecommerce/CouponList';

const CouponListContainer = () => {
  const [filterText, setFilterText] = useState("");
  const [offerListTableDatas, setOfferListTableDatas] = useState<CouponListTableDataColumnType[]>([]);
  
  // ⬇️ PAGINATION STATE VARIABLES
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(false);
  
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editData, setEditData] = useState<any>(null);

  // ⬇️ UPDATED FETCH FUNCTION WITH PAGINATION
  const fetchCoupons = async (page: number, size: number) => {
    try {
      setLoading(true);
      const response = await api.get(
        `/api/Coupan/GetAllCoupanPagination?pageNumber=${page}&pageSize=${size}`
      );
      if (response.status === 200) {
        // ⬇️ response.data.items use karein (assuming same structure as other APIs)
        setOfferListTableDatas(
          response.data.items.map((item: {
            coupanId: any;
            coupanCode: any;
            discountAmount: any;
            expiryDate: any;
          }) => ({
            coupanId: item.coupanId,
            coupanCode: item.coupanCode,
            discountAmount: item.discountAmount,
            expiryDate: item.expiryDate
          }))
        );
        setTotalRows(response.data.totalCount); // ⬅️ Total count set karein
      }
    } catch (error) {
      console.error("Failed to fetch coupon list:", error);
      Swal.fire("Error!", "Failed to fetch coupon list.", "error");
    } finally {
      setLoading(false);
    }
  };

  // ⬇️ USEEFFECT WITH PAGINATION DEPENDENCIES
  useEffect(() => {
    fetchCoupons(currentPage, pageSize);
  }, [currentPage, pageSize]);

  const handleDelete = async (id: number) => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'You will not be able to recover this coupon!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
    }).then((result) => {
      if (result.isConfirmed) {
        confirmDelete(id);
      }
    });
  };

  const confirmDelete = async (id: number) => {
    try {
      await api.delete(`/api/Coupan/DeleteCoupan?coupanId=${id}`);
      Swal.fire('Deleted!', 'Coupon has been deleted.', 'success');
      
      // ⬇️ REFRESH CURRENT PAGE
      await fetchCoupons(currentPage, pageSize);
    } catch (error) {
      Swal.fire('Error!', 'Failed to delete the coupon.', 'error');
      console.error("Delete error:", error);
    }
  }

  const handleEdit = (row: any) => {
    setEditData(row);
    setEditModalOpen(true);
  };

  const handleEditChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditData({ ...editData, [e.target.name]: e.target.value });
  };

  const handleEditSave = async () => {
    if (!editData || !editData.coupanId || !editData.coupanCode || !editData.discountAmount || !editData.expiryDate) {
      Swal.fire("Error!", "Please fill all fields before saving.", "error");
      return;
    }
    if (isNaN(Number(editData.discountAmount)) || Number(editData.discountAmount) <= 0) {
      Swal.fire("Error!", "Please enter a valid discount amount.", "error");
      return;
    }
    if (new Date(editData.expiryDate) < new Date()) {
      Swal.fire("Error!", "Expiry date cannot be in the past.", "error");
      return;
    }
    try {
      await api.put(`/api/Coupan/UpdateCoupan`, {
        coupanId: editData.coupanId,
        coupanCode: editData.coupanCode,
        discountAmount: editData.discountAmount,
        expiryDate: editData.expiryDate,
        isActive: true,
      });
      
      // ⬇️ REFRESH CURRENT PAGE INSTEAD OF LOCAL STATE UPDATE
      await fetchCoupons(currentPage, pageSize);
      
      setEditModalOpen(false);
      Swal.fire("Updated!", "Coupon has been updated.", "success");
    } catch (error) {
      Swal.fire("Error!", "Failed to update the coupon.", "error");
    }
  };

  const couponListTableDataColumn: TableColumn<CouponListTableDataColumnType>[] = [
    {
      name: "Coupon Code",
      cell: (row) => <CouponListTableOfferComponent title={row.coupanCode}/>,
      sortable: true,
      grow: 2,
    },
    {
      name: "Discount Amount",
      selector: (row) => `${row.discountAmount}`,
      sortable: true,
    },
    {
      name: "Expiry Date",
      selector: (row) => {
        if (!row.expiryDate) return "";
        const date = new Date(row.expiryDate);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
      },
      sortable: true,
    },
    {
      name: "Action",
      cell: (row) => (
        <div className="d-flex align-items-center gap-2">
          <span
            onClick={() => handleEdit(row)}
            title="Edit"
            style={{ cursor: "pointer" }}
          >
            <SVG
              iconId="edit-content"
              style={{ fill: "blue", width: 18, height: 18 }}
            />
          </span>
          <span
            onClick={() => handleDelete(Number(row.coupanId))}
            title="Delete"
            style={{ cursor: "pointer" }}
          >
            <SVG
              iconId="trash1"
              style={{ fill: "red", width: 18, height: 18 }}
            />
          </span>
        </div>
      ),
    },
  ];

  // ⬇️ REMOVE filteredItems (frontend search won't work with server pagination)
  // Keep it only if you want local search on current page data
  const filteredItems = offerListTableDatas.filter((item: { coupanCode: any }) => 
    item.coupanCode && item.coupanCode.toLowerCase().includes(filterText.toLowerCase())
  );

  const subHeaderComponentMemo = useMemo(() => {
    return (
      <div className="dataTables_filter d-flex align-items-center">
        <Label className="me-2">{SearchTableButton}:</Label>
        <Input 
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilterText(e.target.value)} 
          type="search" 
          value={filterText} 
        />
      </div>
    );
  }, [filterText]);

  // ⬇️ PAGE CHANGE HANDLERS
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePerRowsChange = (newPerPage: number, page: number) => {
    setPageSize(newPerPage);
    setCurrentPage(page);
  };

  return (
    <Container fluid>
      <Row>
        <Col sm="12">
          <Card>
            <CardBody>
              <div className="list-product-header">
                <CouponListFilterHeader />
              </div>
              <div className="list-product">
                <div className="table-responsive">
                  {/* ⬇️ UPDATED DATATABLE WITH SERVER PAGINATION */}
                  <DataTable 
                    data={filteredItems} // Ya direct offerListTableDatas if no local search needed
                    columns={couponListTableDataColumn} 
                    striped 
                    highlightOnHover 
                    pagination
                    paginationServer // ⬅️ SERVER-SIDE PAGINATION ENABLE
                    paginationTotalRows={totalRows} // ⬅️ TOTAL ROWS FROM API
                    onChangePage={handlePageChange} // ⬅️ PAGE CHANGE HANDLER
                    onChangeRowsPerPage={handlePerRowsChange} // ⬅️ ROWS PER PAGE HANDLER
                    paginationDefaultPage={currentPage} // ⬅️ CURRENT PAGE
                    paginationPerPage={pageSize} // ⬅️ PAGE SIZE
                    progressPending={loading} // ⬅️ LOADING INDICATOR
                    selectableRows 
                    subHeader 
                    subHeaderComponent={subHeaderComponentMemo} 
                  />
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
      
      <Modal
        isOpen={editModalOpen}
        toggle={() => setEditModalOpen(!editModalOpen)}
      >
        <ModalHeader toggle={() => setEditModalOpen(!editModalOpen)}>
          Edit Coupon
        </ModalHeader>
        <ModalBody>
          {editData && (
            <>
              <FormGroup>
                <Label>Coupon Code</Label>
                <Input
                  name="coupanCode"
                  value={editData.coupanCode}
                  onChange={handleEditChange}
                  required={true}
                />
              </FormGroup>
              <FormGroup>
                <Label>Discount Amount</Label>
                <Input
                  name="discountAmount"
                  value={editData.discountAmount}
                  onChange={handleEditChange}
                  required={true}
                />
              </FormGroup>
              <FormGroup>
                <Label>Validity Date</Label>
                <Input
                  type='date'
                  name="expiryDate"
                  value={editData.expiryDate.slice(0, 10)}
                  onChange={handleEditChange}
                  min={new Date().toISOString().slice(0, 10)}
                  required={true}
                />
              </FormGroup>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setEditModalOpen(false)}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleEditSave}>
            Save
          </Button>
        </ModalFooter>
      </Modal>
    </Container>
  )
}

export default CouponListContainer